/**
 * The permission-hash catalog — the axes of the role/permission matrix.
 *
 * `Permission-Catalog.json` is a verbatim mirror of the home app's `static_permissions.json`, which is in
 * turn the same set the resolver hardcodes (`api/permissions/.../EndpointBase.js`). A permission hash is
 * `"<Entity>-<Action>"`; the matrix is rows = entities (~150) x cols = the 10 actions, with a cell only
 * togglable where the entity declares that action. No pict / DOM / network dependencies — pure data + string
 * helpers so the matrix builder and its tests run in isolation.
 */

const CATALOG = require('./Permission-Catalog.json');

const AVAILABLE_ACTIONS = CATALOG.AvailableActions;
const PERMISSION_MAP = CATALOG.AvailablePermissionMap;

// Report/form permissions are DYNAMIC rows (not in the static catalog): a permission hash
// `DocumentType-<ReportNamedInstance.Hash>-<Action>`, where <Action> is Create/View/Update/Delete OR a
// compound `View-<WorkflowStatus>` (e.g. View-Approved). The reports catalog source (Access-Matrix-Builder)
// enumerates these from the live ReportNamedInstance set; here we only need to parse them.
const REPORT_ENTITY_PREFIX = 'DocumentType-';

// The wire Context values the permission endpoints speak vs. the membership keys the seed catalog
// (CustomerProvisioning-RoleCatalog) uses. Kept here as the single source of truth (plan risk #4).
const CONTEXT_TO_MEMBERSHIP = { Customer: 'CustomerMembership', Contract: 'ContractMembership', Project: 'ProjectMembership' };
const MEMBERSHIP_TO_CONTEXT = { CustomerMembership: 'Customer', ContractMembership: 'Contract', ProjectMembership: 'Project' };

/**
 * Split a permission hash into its entity + action. The action is the final `-`-delimited segment; the
 * entity is everything before it (so association hashes like `BidItem-Organization-Associate` parse to
 * entity `BidItem-Organization`, action `Associate`).
 * @param {string} pHash
 * @returns {{ Entity: string, Action: string }}
 */
function parseHash(pHash)
{
	const tmpHash = String(pHash || '');
	// Report/form hashes (`DocumentType-<RNI.Hash>-<Action>`) need special handling: the RNI hash itself can
	// contain dashes, and the action may be a compound `View-<Status>`. Mirror the home app's rule
	// (PermissionsManager.vue): if the second-to-last segment is `View`, the action is those last two
	// segments; otherwise it is the last segment. The entity is everything before the action.
	if (tmpHash.indexOf(REPORT_ENTITY_PREFIX) === 0)
	{
		const tmpTokens = tmpHash.split('-');
		const tmpAction = (tmpTokens.length >= 2 && tmpTokens[tmpTokens.length - 2] === 'View')
			? `${tmpTokens[tmpTokens.length - 2]}-${tmpTokens[tmpTokens.length - 1]}`
			: tmpTokens[tmpTokens.length - 1];
		return { Entity: tmpHash.slice(0, tmpHash.length - tmpAction.length - 1), Action: tmpAction };
	}
	const tmpIndex = tmpHash.lastIndexOf('-');
	if (tmpIndex < 0)
	{
		return { Entity: tmpHash, Action: '' };
	}
	return { Entity: tmpHash.slice(0, tmpIndex), Action: tmpHash.slice(tmpIndex + 1) };
}

/**
 * Is this a report/form permission entity (a `DocumentType-<RNI.Hash>` row)?
 * @param {string} pEntity
 * @returns {boolean}
 */
function isReportRow(pEntity)
{
	return String(pEntity || '').indexOf(REPORT_ENTITY_PREFIX) === 0;
}

/**
 * Compose a permission hash from an entity + action.
 * @param {string} pEntity
 * @param {string} pAction
 * @returns {string}
 */
function buildHash(pEntity, pAction)
{
	return `${pEntity}-${pAction}`;
}

/**
 * The actions a given entity declares (the togglable cells in its matrix row).
 * @param {string} pEntity
 * @returns {Array<string>}
 */
function actionsForEntity(pEntity)
{
	return PERMISSION_MAP[pEntity] || [];
}

/**
 * Does an entity declare an action (i.e. is that matrix cell real, not a dash)?
 * @param {string} pEntity
 * @param {string} pAction
 * @returns {boolean}
 */
function entityHasAction(pEntity, pAction)
{
	return actionsForEntity(pEntity).indexOf(pAction) > -1;
}

/**
 * Every entity in the catalog, in declaration order.
 * @returns {Array<string>}
 */
function entityList()
{
	return Object.keys(PERMISSION_MAP);
}

/**
 * A `Module-*` access row (e.g. `Module-LIMS`) — only declares the `Access` action.
 * @param {string} pEntity
 * @returns {boolean}
 */
function isModuleRow(pEntity)
{
	return String(pEntity || '').indexOf('Module-') === 0;
}

/**
 * An association row (an `X-Y` link entity) — declares `Associate`/`Disassociate` rather than CRUD.
 * @param {string} pEntity
 * @returns {boolean}
 */
function isAssociationRow(pEntity)
{
	return actionsForEntity(pEntity).indexOf('Associate') > -1;
}

/**
 * Bucket an entity into the matrix section it belongs to.
 * @param {string} pEntity
 * @returns {'module'|'association'|'entity'}
 */
function rowKind(pEntity)
{
	if (isModuleRow(pEntity))
	{
		return 'module';
	}
	if (isAssociationRow(pEntity))
	{
		return 'association';
	}
	return 'entity';
}

/**
 * Map a wire Context to the seed-catalog membership key (Customer -> CustomerMembership, ...).
 * @param {string} pContext
 * @returns {string}
 */
function contextToMembership(pContext)
{
	return CONTEXT_TO_MEMBERSHIP[pContext] || pContext;
}

/**
 * Map a seed-catalog membership key back to its wire Context.
 * @param {string} pMembership
 * @returns {string}
 */
function membershipToContext(pMembership)
{
	return MEMBERSHIP_TO_CONTEXT[pMembership] || pMembership;
}

module.exports =
{
	AVAILABLE_ACTIONS,
	PERMISSION_MAP,
	CONTEXT_TO_MEMBERSHIP,
	MEMBERSHIP_TO_CONTEXT,
	REPORT_ENTITY_PREFIX,
	parseHash,
	buildHash,
	actionsForEntity,
	entityHasAction,
	entityList,
	isModuleRow,
	isAssociationRow,
	isReportRow,
	rowKind,
	contextToMembership,
	membershipToContext,
};
